import type { SyncOp } from '@ims/core'
import { tablesByName, type TableName } from './tables.ts'
import { writableSchema } from './zod.ts'

/** Tables only the server writes (sync_table_config.client_writable = false). */
export const SERVER_ONLY_TABLES: ReadonlySet<TableName> = new Set([
  'billing_counters',
  'counter_sequences',
])

export function isTableName(name: string): name is TableName {
  return Object.hasOwn(tablesByName, name)
}

export interface OpIssue {
  index: number
  table: string
  message: string
}

/**
 * Checks ops before they are applied locally or pushed, so malformed rows fail
 * at the point of the user action instead of later in sync. Business rules
 * (roles, limits, immutability) are still enforced by the server.
 */
export function validateOps(ops: readonly SyncOp[]): OpIssue[] {
  const issues: OpIssue[] = []
  ops.forEach((op, index) => {
    if (!isTableName(op.table)) {
      issues.push({ index, table: op.table, message: 'unknown table' })
      return
    }
    if (SERVER_ONLY_TABLES.has(op.table)) {
      issues.push({ index, table: op.table, message: 'table is written by the server only' })
      return
    }
    if (op.op === 'delete') {
      if (typeof op.row.id !== 'string')
        issues.push({ index, table: op.table, message: 'delete needs row.id' })
      return
    }
    const result = writableSchema(op.table).safeParse(op.row)
    if (!result.success) {
      for (const issue of result.error.issues) {
        issues.push({
          index,
          table: op.table,
          message: `${issue.path.join('.') || 'row'}: ${issue.message}`,
        })
      }
    }
  })
  return issues
}
